import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatGBP, toInputDateUK, apiError } from "@/lib/api";
import DocumentUpload from "@/components/DocumentUpload";
import DuplicateDialog, { conflictFrom } from "@/components/DuplicateDialog";
import ConfirmDetails from "@/components/ConfirmDetails";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Save, Send, AlertTriangle, Loader2 } from "lucide-react";
import PaymentChoice, { paymentPayload, paidAmount, todayUK } from "@/components/PaymentChoice";
import { useFeatures } from "@/lib/features";

export default function EditPurchasePage() {
  const { id } = useParams();
  const { activeStoreId, stores } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(2); // Start on details since doc already uploaded
  useEffect(() => { document.querySelector("[data-testid=main-scroll]")?.scrollTo(0, 0); }, [step]);
  const [documents, setDocuments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newSupplier, setNewSupplier] = useState(false);

  const [form, setForm] = useState({
    store_id: "", supplier_id: "", supplier_name: "",
    invoice_number: "", invoice_date: "", subtotal: "", vat_amount: "", total_amount: "",
    product_details: "", payment_status: "unpaid", payment_method: "", amount_paid: "",
    is_credit_note: false, notes: "",
  });

  useEffect(() => {
    let live = true;
    Promise.all([
      api.get(`/purchases/${id}`),
      api.get("/suppliers"),
    ]).then(([pRes, sRes]) => {
      if (!live) return;
      const p = pRes.data;
      if (!["draft", "rejected"].includes(p.status)) { navigate(`/view-purchase/${id}`, { replace: true }); return; }
      setSuppliers(sRes.data);
      setForm({
        store_id: String(p.store_id), supplier_id: p.supplier_id ? String(p.supplier_id) : "",
        supplier_name: "", invoice_number: p.invoice_number || "", invoice_date: toInputDateUK(p.invoice_date),
        subtotal: p.subtotal || "", vat_amount: p.vat_amount || "", total_amount: p.total_amount || "",
        product_details: p.product_details || "", payment_status: p.payment_status || "unpaid",
        payment_method: p.payment_method || "", amount_paid: p.amount_paid || "",
        payment_date: p.payment_date ? toInputDateUK(p.payment_date) : todayUK(), paid_amount: "",
        is_credit_note: p.is_credit_note || false, notes: p.notes || "",
      });
      // Restore document references
      if (p.document_ids?.length) {
        setDocuments(p.document_ids.map((did) => {
          const docInfo = p.documents?.find((d) => d.id === did);
          return { id: did, original_filename: docInfo?.original_filename || `Document ${did}` };
        }));
      }
      setLoading(false);
    }).catch(() => {
      toast.error("Could not load purchase");
      navigate("/submissions");
    });
  }, [id, navigate]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const { phase2 } = useFeatures();

  const [confirmed, setConfirmed] = useState(false);
  const [conflict, setConflict] = useState(null);

  const save = async (submit = false, extra = {}) => {
    setSaving(true);
    try {
      const payload = {
        supplier_id: newSupplier ? null : (form.supplier_id ? parseInt(form.supplier_id) : null),
        supplier_name: newSupplier ? form.supplier_name : null,
        invoice_number: form.invoice_number, invoice_date: form.invoice_date,
        subtotal: parseFloat(form.subtotal) || 0, vat_amount: parseFloat(form.vat_amount) || 0,
        total_amount: parseFloat(form.total_amount) || 0, product_details: form.product_details,
        ...paymentPayload(form, phase2), document_ids: documents.map((d) => d.id),
        notes: form.notes, is_credit_note: form.is_credit_note, ...extra,
      };
      await api.put(`/purchases/${id}`, payload);
      setConflict(null);
      if (submit && !extra.duplicate_review_reason) {
        const r = await api.patch(`/purchases/${id}/submit`, { confirmed, acknowledge_cross_store: !!extra.acknowledge_cross_store, paid_amount: paidAmount(form) });
        toast.success(r.data.message || "Purchase submitted");
      } else {
        toast.success(extra.duplicate_review_reason ? "Draft saved — waiting for a manager to review the duplicate" : "Draft updated");
      }
      navigate("/submissions");
    } catch (err) {
      const c = conflictFrom(err);
      if (c) setConflict({ ...c, submit, extra });
      else toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  const steps = ["Upload Invoice", "Invoice Details", "Payment & Submit"];

  return (
    <div className="space-y-5" data-testid="edit-purchase-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/submissions")} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-navy/40 hover:text-navy" aria-label="Back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-heading font-bold text-navy">Edit Purchase</h1>
      </div>

      <StepIndicator steps={steps} current={step} />

      {step === 1 && (
        <div className="space-y-4">
          <p className="text-sm text-navy/50">Upload additional documents or proceed</p>
          {documents.length > 0 && (
            <div className="bg-white rounded-xl border border-navy/10 p-3">
              <p className="text-sm text-navy font-medium">{documents.length} document(s) attached</p>
              {documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-2">
                  <p className="text-xs text-navy/50 truncate">{d.original_filename || `Doc #${d.id}`}</p>
                  <button type="button" onClick={() => setDocuments((p) => p.filter((x) => x.id !== d.id))} className="min-h-[44px] px-3 text-xs text-red-500" data-testid={`detach-document-${d.id}-btn`}>Remove</button>
                </div>
              ))}
            </div>
          )}
          <DocumentUpload kind="invoice" storeId={parseInt(form.store_id)} onUpload={(doc) => setDocuments((p) => [...p, doc])} onRemove={(did) => setDocuments((p) => p.filter((x) => x.id !== did))} />
          <button onClick={() => setStep(2)} data-testid="step1-next-btn" className="w-full gm-btn-gold flex items-center justify-center gap-2">Next <ArrowRight className="w-4 h-4" /></button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-navy">Supplier</Label>
            {!newSupplier ? (
              <div className="flex gap-2">
                <Select value={form.supplier_id} onValueChange={(v) => set("supplier_id", v)}>
                  <SelectTrigger className="gm-input flex-1" data-testid="supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
                <button type="button" onClick={() => setNewSupplier(true)} className="text-xs text-gold px-2">+ New</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input value={form.supplier_name} onChange={(e) => set("supplier_name", e.target.value)} placeholder="New supplier name" className="gm-input flex-1" data-testid="new-supplier-input" />
                <button type="button" onClick={() => setNewSupplier(false)} className="text-xs text-navy/40 px-2">Cancel</button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Invoice No."><Input value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} className="gm-input" data-testid="invoice-number-input" /></Field>
            <Field label="Invoice Date"><Input value={form.invoice_date} onChange={(e) => set("invoice_date", e.target.value)} placeholder="DD/MM/YYYY" className="gm-input" data-testid="invoice-date-input" /></Field>
          </div>
          <Field label="Product Details"><Textarea value={form.product_details} onChange={(e) => set("product_details", e.target.value)} rows={3} className="gm-input" /></Field>
          <div className="grid grid-cols-3 gap-2.5">
            <Field label="Subtotal (£)"><Input type="number" step="0.01" value={form.subtotal} onChange={(e) => set("subtotal", e.target.value)} className="gm-input font-mono" data-testid="purchase-subtotal-input" /></Field>
            <Field label="VAT (£)"><Input type="number" step="0.01" value={form.vat_amount} onChange={(e) => set("vat_amount", e.target.value)} className="gm-input font-mono" data-testid="purchase-vat-input" /></Field>
            <Field label="Total (£)"><Input type="number" step="0.01" value={form.total_amount} onChange={(e) => set("total_amount", e.target.value)} className="gm-input font-mono font-bold" data-testid="purchase-amount-input" /></Field>
          </div>
          <Separator />
          <div className="flex gap-3">
            <button onClick={() => setStep(1)} data-testid="step2-back-btn" className="flex-1 gm-btn-navy flex items-center justify-center gap-2"><ArrowLeft className="w-4 h-4" /> Back</button>
            <button onClick={() => setStep(3)} data-testid="step2-next-btn" className="flex-1 gm-btn-gold flex items-center justify-center gap-2">Next <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <PaymentChoice form={form} set={set} />
          <Field label="Notes"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="gm-input" /></Field>
          <Card className="border-navy/10 shadow-none"><CardContent className="p-4">
            <h3 className="font-semibold text-navy text-sm mb-2">Summary</h3>
            <div className="grid grid-cols-2 gap-y-1.5 text-sm">
              <span className="text-navy/50">Total</span><span className="font-mono font-bold text-navy">{formatGBP(form.total_amount)}</span>
              <span className="text-navy/50">Documents</span><span className="text-navy">{documents.length} attached</span>
            </div>
          </CardContent></Card>
          <Separator />
          <div className="flex gap-3">
            <button onClick={() => setStep(2)} data-testid="step3-back-btn" className="flex-1 gm-btn-navy flex items-center justify-center gap-2 text-sm"><ArrowLeft className="w-4 h-4" /> Back</button>
          </div>
          <div className="flex flex-col gap-2 sticky bottom-0 z-10 bg-cream -mx-4 px-4 pt-3 pb-3 border-t border-navy/10" data-testid="sticky-actions">
            <ConfirmDetails checked={confirmed} onChange={setConfirmed} kind="invoice" />
            <div className="flex gap-3">
            <button onClick={() => save(false)} disabled={saving} className="flex-1 gm-btn-navy flex items-center justify-center gap-2 text-sm" data-testid="save-draft-btn"><Save className="w-4 h-4" /> Update Draft</button>
            <button onClick={() => save(true)} disabled={saving || documents.length === 0 || !confirmed} className="flex-1 gm-btn-gold flex items-center justify-center gap-2 text-sm" data-testid="submit-submission-button"><Send className="w-4 h-4" /> Submit</button>
            </div>
          </div>
          {documents.length === 0 && <p className="text-xs text-amber-600 text-center flex items-center justify-center gap-1"><AlertTriangle className="w-3 h-3" /> Upload a document to submit</p>}
        </div>
      )}
      <DuplicateDialog conflict={conflict} busy={saving} onClose={() => setConflict(null)}
        onRequestException={(reason) => save(false, { ...conflict.extra, duplicate_review_reason: reason })}
        onContinue={() => save(conflict.submit, { ...conflict.extra, acknowledge_cross_store: true })} />
    </div>
  );
}

function Field({ label, children }) {
  return (<div className="space-y-1.5"><Label className="text-sm font-medium text-navy">{label}</Label>{children}</div>);
}
function StepIndicator({ steps, current }) {
  return (<div className="flex items-center gap-1.5">{steps.map((s, i) => (<div key={s} className="flex items-center gap-1.5 flex-1"><div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i+1<current?"gm-step-completed":i+1===current?"gm-step-active":"gm-step-pending"}`}>{i+1<current?<Check className="w-3.5 h-3.5"/>:i+1}</div><span className={`text-[11px] hidden sm:inline ${i+1===current?"text-navy font-medium":"text-navy/30"}`}>{s}</span>{i<steps.length-1&&<div className="flex-1 h-px bg-navy/10"/>}</div>))}</div>);
}
